import { AxiosRequestConfig, AxiosResponse } from "axios";

import { format } from "date-fns";
import { API_CLIENT } from "./config";

function safeBasename(
  str: string | null | undefined,
  defaultVal: string
): string {
  if (str == null) return defaultVal;

  let s = String(str);
  if (s === "") return defaultVal;
  s = s.trim();

  // 文字クラス判定：Unicode の Letter / Decimal_Number
  const isLetterOrDigit = (ch: string) => /\p{L}|\p{Nd}/u.test(ch);

  let out = "";
  for (const ch of s) {
    // NOTE: for-of はコードポイント単位で回る
    const cp = ch.codePointAt(0)!;

    if (ch === "." || ch === "-" || ch === "_" || ch === " ") {
      out += ch; // そのまま許可
    } else if ('/\\:*?"<>|'.includes(ch)) {
      out += "-"; // Windows 禁止文字
    } else if (cp === 0x7f || cp < 0x20) {
      out += "-"; // 制御文字
    } else if (isLetterOrDigit(ch)) {
      out += ch; // Unicode 文字/数字を保持
    } else {
      out += "-"; // その他（絵文字など）は "-" に寄せる
    }
  }

  let name = out.trim();
  name = name.replace(/ /g, "-").replace(/\-+/g, "-").replace(/_+/g, "_");

  // 先頭/末尾の危険な記号を除去、Windows が嫌う末尾の "." / " " を除去
  name = name.replace(/^[-_]+|[-_]+$/g, "");
  name = name.replace(/[. ]+$/g, "");

  // "." / ".." / 空 は却下
  if (name === "." || name === ".." || name === "") {
    return defaultVal;
  }

  if (name === "") return defaultVal;
  return name;
}

// 実際にcsvをダウンロードするDOM要素を作成
const dlAnchor = document.createElement("a");
document.body.appendChild(dlAnchor);
dlAnchor.style.display = "none";

/**
 * Blobをファイルとしてダウンロードさせる共通関数
 *
 * @param blob ダウンロードするBlob
 * @param fileName ファイル名（拡張子なし）
 * @param extension ファイルの拡張子
 */
export function downloadBlobAsFile(
  blob: Blob,
  fileName: string,
  extension: string
): void {
  const blobURL = window.URL.createObjectURL(blob);
  const executionDateTime = format(new Date(), "yyyyMMddHHmm");

  dlAnchor.href = blobURL;
  dlAnchor.download = safeBasename(
    fileName
      ? `${fileName}_${executionDateTime}.${extension}`
      : `data.${extension}`,
    "download.dat"
  );

  dlAnchor.click();
  window.URL.revokeObjectURL(blobURL);
}

export async function downloadFileFromAPI(
  fetchURL: string,
  fileName: string,
  dataType: string,
  config?: AxiosRequestConfig | undefined
): Promise<AxiosResponse<unknown>> {
  /**
   * APIを叩いてファイルをDLさせる関数
   *
   * @param fetchURL 対象のAPI URL
   * @param dataType ファイルタイプ。拡張子として利用
   * @param config axiosで利用可能な設定値オブジェクト。クエリパラメータなど
   * @param fileName ファイル名。指定しない場合はデフォルトのファイル名が設定される
   *
   */

  // バイナリファイル(blob型)としてデータを受け取り、あらゆるファイルを処理できることを明示して送信
  const headers = config?.headers;
  const res = await API_CLIENT.get<Blob>(fetchURL, {
    ...config,
    responseType: "blob",
    headers: { ...headers, accept: "*/*" },
  });

  // content-dispositionヘッダーからファイル名を取得する特殊ケース
  if (res.headers?.["content-disposition"] && !fileName) {
    const match = res.headers["content-disposition"].match(
      /filename=("(?:[^\\"]|\\\\|\\")+"|'(?:[^\\']|\\\\|\\')+'|[^;]+)/
    );
    if (match) {
      const blobURL = window.URL.createObjectURL(res.data);
      dlAnchor.href = blobURL;
      dlAnchor.download = decodeURIComponent(
        match[1]
          .replace(/^"(.+)"$/, (_: string, c: string) => c.replace('\\"', '"'))
          .replace(/^'(.+)'$/, (_: string, c: string) => c.replace("\\'", "'"))
      );
      dlAnchor.click();
      window.URL.revokeObjectURL(blobURL);
      return res;
    }
  }

  downloadBlobAsFile(res.data, fileName, dataType);
  return res;
}

/**
 * `downloadFileFromAPI` の POST 版。リクエストボディで条件を渡すダウンロード API 用。
 * GET 版と違い content-disposition は見ず、常に `fileName` を使う。
 *
 * @param fetchURL 対象のAPI URL
 * @param body リクエストボディ
 * @param fileName ファイル名(拡張子なし)
 * @param dataType ファイルタイプ。拡張子として利用
 * @param config axiosで利用可能な設定値オブジェクト。クエリパラメータなど
 */
export async function postDownloadFileFromAPI(
  fetchURL: string,
  body: unknown,
  fileName: string,
  dataType: string,
  config?: AxiosRequestConfig | undefined
): Promise<AxiosResponse<Blob>> {
  const headers = config?.headers;
  const res = await API_CLIENT.post<Blob>(fetchURL, body, {
    ...config,
    responseType: "blob",
    headers: { ...headers, accept: "*/*" },
  });
  downloadBlobAsFile(res.data, fileName, dataType);
  return res;
}

// TODO(sfujiwara): `errorCode` は "不明なステータス" のような文が入ったり
// 500 のような HTTP ステータスコードが入ったりと一貫性がないので、文だけが入るように統一した方が良い。
// https://github.com/algo-artis/spreadsheet-ui-frontend/pull/2794
export const getAPIErrorDetailOrMessage = (error: unknown) => {
  let errorMessage = "不明なエラー";
  let errorCode = "不明なステータス";
  let httpStatus: number | undefined;
  if (typeof error === "object" && error !== null) {
    const errorObj = error as { [key: string]: any };
    errorMessage =
      errorObj?.response?.data?.detail ?? errorObj.message ?? errorMessage;
    httpStatus = errorObj?.response?.status;
    errorCode =
      typeof httpStatus !== "undefined" ? String(httpStatus) : errorCode;
  }
  return { errorMessage, errorCode, httpStatus };
};

export const getAPIErrorDetailAndMessage = (error: unknown) => {
  if (typeof error === "object" && error !== null) {
    const errorObj = error as { [key: string]: any };
    const detail = errorObj?.response?.data?.detail ?? "";
    const message = errorObj?.message ?? "";
    return detail + message;
  }
  return "";
};

export const getAPIConfigs = (organizationId?: string) => {
  const apiOrganizationIdParams = organizationId
    ? {
        params: {
          org_id: organizationId,
        },
      }
    : undefined;
  const apiHeadersWithJson = {
    headers: {
      "Content-Type": "application/json",
    },
  };
  return { apiOrganizationIdParams, apiHeadersWithJson };
};
